import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import IOnProcessOrderEntryItemsDTO from "@models/DTO/IOnProcessOrderEntryItemsDTO";
import IOnTypeaheadCatalogItemDTO from "@models/DTO/IOnTypeaheadCatalogItemDTO";
import ITypeaheadCatalogItemDTO from "@models/DTO/ITypeaheadCatalogItemDTO";
import EndpointsEnum from "@models/EndpointsEnum";
import ICatalog_Item from "@models/ICatalog_Item";
import INotification from "@models/INotification";
import IOrder from "@models/IOrder";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import OwButton from "@components/shared/OwButton/OwButton";
import OwDebouncedInput from "@components/shared/OwDebouncedInput/OwDebouncedInput";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import { SectionIndex } from "@components/shared/SectionIndex/SectionIndex";
import CatalogViewerCart from "../CatalogViewerCart/CatalogViewerCart";
import CatalogViewerItem from "../CatalogViewerItem/CatalogViewerItem";
import "./CatalogViewerSidebar.scss";
import IGetChildItemsDTO from "@models/DTO/IGetChildItemsDTO";
import IOnGetChildItemsDTO from "@models/DTO/IOnGetChildItemsDTO";
import IWarehouse from "@models/IWarehouse";
import IUiSocketSetup from "@models/IUiSocketSetup";
import UserContext from "@contexts/UserContext";
import SupplierContext from "@contexts/SupplierContext";
import VendorContext from "@contexts/VendorContext";
import { SocketContext } from "@contexts/SocketContext";
import UiContext from "@contexts/UiContext";
import { IAddBulkItems } from "@models/IAddBulkItems";
import IOrderRowConfig from "@models/IOrderRowConfig";
import OrderRowColPropertyEnum from "@models/OrderRowColPropertyEnum";
import OrderEntryService from "@services/OrderEntryService";

type Props = {
    cancel: () => void;
    label: string;
    addToOrder: (items: IAddBulkItems[]) => void;
    order: IOrder;
    isOnline: boolean;
    isOfflineOrder: boolean;
    isChildItemsOnly?: boolean;
    parentCatalogItemId?: number;
    warehouses: IWarehouse[];
};

const CatalogViewerSidebar: React.FC<Props> = ({
    cancel,
    label,
    addToOrder,
    order,
    isOnline,
    isOfflineOrder,
    isChildItemsOnly,
    parentCatalogItemId,
    warehouses,
}) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);

    const catalogViewRef = useRef<HTMLDivElement>(null);

    const [offset, setOffset] = useState(0);
    const limit = useMemo(() => 25, []);
    const [catalogItems, setCatalogItems] = useState<any[]>([]);
    const [showLoadMore, setShowLoadMore] = useState(false);
    const [loadMoreActive, setLoadMoreActive] = useState(false);
    const [showFilterInput, setShowFilterInput] = useState(true);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [singleWildcard, setSingleWildcard] = useState(false);
    const [initalSearch, setInitalSearch] = useState(true);
    const [itemsToAdd, setItemsToAdd] = useState<IAddBulkItems[]>([]);

    const hasEditableDimensions = useMemo(() => {
        return OrderEntryService.hasEditableDimensions(order, supplierModel);
    }, [order.Catalog, supplierModel.length_for_depth]);

    const onProcessOrderEntryItems = useCallback(
        (data: IOnProcessOrderEntryItemsDTO) => {
            cancel();
        },
        [cancel]
    );

    const onGetChildItems = useCallback((data: IOnGetChildItemsDTO) => {
        setCatalogItems(data.childItems);
    }, []);

    const onTypeaheadCatalogItem = useCallback(
        (data: IOnTypeaheadCatalogItemDTO) => {
            let updatedCatalogItems = [];

            // if the offset is 0, we assume that it's a new
            // starting index value and that we want to reset the rows
            // to just this index.
            // if its not 0, we assume that it should be appended.
            if (offset === 0) {
                updatedCatalogItems = data.catalog_items;
            } else {
                updatedCatalogItems = [...(catalogItems || []), ...data.catalog_items];
            }

            let shouldShowLoadMore = false;

            if (data.totalCatalogItems - updatedCatalogItems.length > 0) {
                shouldShowLoadMore = true;
            }

            setCatalogItems(updatedCatalogItems);
            setShowLoadMore(shouldShowLoadMore);
            setLoadMoreActive(false);
            setInitalSearch(false);

            if (offset === 0) {
                if (catalogViewRef.current) {
                    catalogViewRef.current.scrollTop = 0;
                }
            }
        },
        [catalogItems, loadMoreActive, showLoadMore, offset, initalSearch]
    );

    const sockets: IUiSocketSetup[] = useMemo(
        () => [
            { endpoint: EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, fn: onTypeaheadCatalogItem },
            { endpoint: EndpointsEnum.OrderEntryOnProcessOrderEntryItems, fn: onProcessOrderEntryItems },
            { endpoint: EndpointsEnum.CatalogItemOnGetChildItems, fn: onGetChildItems },
        ],
        [offset, catalogItems]
    );

    useEffect(() => {
        for (const socketSetup of sockets) {
            socket.on(socketSetup.endpoint, socketSetup.fn);
        }

        return () => {
            for (const socketSetup of sockets) {
                socket.off(socketSetup.endpoint, socketSetup.fn);
            }
        };
    }, [sockets]);

    useEffect(() => {
        performCatalogItemLookup(searchTerm, singleWildcard);
    }, []);

    const handleFilterInput = (e: any) => {
        const inputValue = e.target.value;
        setSearchTerm(inputValue);
        setSingleWildcard(true);
        setOffset(0);
        // setOffset(0)
    };

    const performCatalogItemLookup = async (name: string, singleWildcard?: boolean) => {
        if (isChildItemsOnly && parentCatalogItemId) {
            const payload: IGetChildItemsDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: order.Vendor!.guid,
                parentCatalogItemId: parentCatalogItemId,
            };

            socket.emit(EndpointsEnum.CatalogItemGetChildItems, payload);
        } else {
            if (!order.Catalog) {
                return;
            }

            const data: ITypeaheadCatalogItemDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: order.Vendor!.guid,
                catalogGuid: order.Catalog.guid,
                orderGuid: order.guid,
                name,
                singleWildcard,
                limit: limit,
                offset: offset,
                onlyActive: true,
                includeTotal: true,
                options: {
                    searchName: true,
                    searchDescription: true,
                    searchSku: true,
                    searchUpc: true,
                },
            };

            if (initalSearch) {
                // maybe we want to do something differently with this?
                data.onlyCommonItems = false;
            }

            socket.emit(EndpointsEnum.CatalogItemTypeaheadCatalogItem, data);
        }
    };

    useEffect(() => {
        if (searchTerm.length) {
            performCatalogItemLookup(searchTerm!, singleWildcard);
        }
    }, [offset, searchTerm]);

    const catalogLookupByIndex = async (sectionIndexValue: string, shouldIncrOffset?: boolean, _singleWildcard?: boolean) => {
        setSearchTerm(sectionIndexValue);
        setSingleWildcard(false);
        setLoadMoreActive(true);

        if (searchTerm !== sectionIndexValue) {
            setOffset(0);
        }
    };

    const loadMore = (e: any) => {
        const newOffset = offset + 1;
        setOffset(newOffset);
    };

    const toggleInputFilter = () => {
        setShowFilterInput((prevState) => !prevState);
    };

    const handleAddToOrder = (items: IAddBulkItems[]) => {
        const _itemsToAdd: IAddBulkItems[] = [];

        for (const itemToAdd of items) {
            let found = false;

            if (supplierModel.no_dupe_order_items) {
                for (const existingItem of order.Order_Items) {
                    if (Number(itemToAdd.item.catalog_item_id) === Number(existingItem.catalog_item_id)) {
                        found = true;
                    }
                }
            }

            if (!found) {
                _itemsToAdd.push(itemToAdd);
            } else {
                const notification: INotification = {
                    title: "item_already_in_cart",
                    message: itemToAdd.item.Catalog_Item?.name || "",
                    position: NotificationPositionEnum.bc,
                    level: NotificationLevelEnum.error,
                };

                uiContext.notifyFn(null, notification);
            }
        }

        if (_itemsToAdd.length) {
            const updatedItemsToAdd = [...itemsToAdd, ..._itemsToAdd];
            setItemsToAdd(updatedItemsToAdd);
        }
    };

    const removeCartItem = (item: ICatalog_Item, index: number) => {
        const newCartItems = [...itemsToAdd];
        newCartItems.splice(index, 1);
        setItemsToAdd(newCartItems);
    };

    const addCartToOrder = () => {
        addToOrder(itemsToAdd);
    };

    return (
        <div className="CatalogViewerSidebar container p-0 d-flex flex-column" style={{ height: "100%", overflow: "hidden" }}>
            {/* Fixed Header Section */}
            <div className={"d-flex flex-column p-2 flex-shrink-0"}>
                <div className={"d-flex justify-content-between align-items-center"}>
                    <OwTitle className={"me-auto"} leftIconName={"far fa-book"} title={label} />
                    <div>
                        <div className="text-end">
                            <button className="btn btn-danger btn-sm" onClick={cancel}>
                                <i className="far fa-times" />
                            </button>
                        </div>
                        <span
                            onClick={toggleInputFilter}
                            className={"mt-2 pointer d-inline-flex fas fa-filter " + (showFilterInput ? "text-warning" : "")}
                        />
                    </div>
                </div>
            </div>

            {/* Fixed Search Section */}
            <div className={"d-flex p-2 flex-shrink-0 " + (showFilterInput ? "d-block" : "d-none")}>
                <div className={"col-12 position-relative"}>
                    <hr />
                    <OwDebouncedInput
                        title={"sectionIndexFilterInput"}
                        performingSearch={loadMoreActive}
                        fn={handleFilterInput}
                        value={searchTerm || ""}
                        placeholder={"Search Catalog Items"}
                    />
                    <hr />
                </div>
            </div>

            {/* Flexible Main Content Area */}
            <div className="main-content d-flex flex-row flex-grow-1" style={{ minHeight: 0, overflow: "hidden" }}>
                {/* Section Index - Fixed Width, Scrollable */}
                <div className={"p-2 flex-shrink-0"} style={{ width: "60px", overflowY: "auto", height: "100%" }}>
                    <SectionIndex
                        selectedIndexFn={(e) => {
                            catalogLookupByIndex(e, undefined, true);
                        }}
                    />
                </div>

                {/* Catalog Items - Flexible Width, Scrollable */}
                <div className={"d-flex flex-column flex-grow-1"} style={{ minHeight: 0 }}>
                    <div className="p-2 flex-grow-1" style={{ overflowY: "auto" }}>
                        <div ref={catalogViewRef} className={"catalogItems"}>
                            {catalogItems?.length ? (
                                catalogItems.map((item, index) => {
                                    return (
                                        <CatalogViewerItem
                                            key={item.guid}
                                            catalogItem={item}
                                            index={index}
                                            addToOrder={handleAddToOrder}
                                            order={order}
                                            socket={socket}
                                            userModel={userModel}
                                            supplierModel={supplierModel}
                                            vendorModel={vendorModel}
                                            notifyFn={uiContext.notifyFn}
                                            warehouses={warehouses}
                                            hasEditableDimensions={hasEditableDimensions}
                                        />
                                    );
                                })
                            ) : (
                                <div className="d-flex w-200 h-100 align-items-center justify-content-center">
                                    <OwTitle title={"no_catalog_items_found"} tag={"h4"} leftIconName={"fas fa-shelves-empty"} />
                                </div>
                            )}
                            {showLoadMore ? (
                                <div className={"d-flex w-100 mt-2 justify-content-center align-items-center text-center"}>
                                    <OwButton
                                        title={"load_more"}
                                        fn={loadMore}
                                        active={loadMoreActive}
                                        disabled={loadMoreActive}
                                        className={"btn-primary"}
                                    />
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>

            {/* Fixed Cart Section */}
            <div className="flex-shrink-0">
                <CatalogViewerCart
                    order={order}
                    items={itemsToAdd}
                    removeCartItem={removeCartItem}
                    addCartToOrder={addCartToOrder}
                    supplierModel={supplierModel}
                    userModel={userModel}
                />
            </div>
        </div>
    );
};

export default CatalogViewerSidebar;
